import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect, useRef } from 'react';
import { 
    setActiveElevation, 
    setActiveScene, 
    nextStep, 
    previousStep, 
    jumpToStep,
    toggleAppliedProducts,
    selectCurrentStep,
    selectProgressPercentage
} from '../../../store/visualizerSlice';
import { FloorplanDataCard, TopMenu, RoomSceneSwitcher, Visualizer, Progress, Roof, Generic, Cabinets, IslandCabinets, Siding, SidingAccent, ExtDoors, ExtShutters, Stone, Brick, ExtTrim, ExtWindowTrim, Floor, BathFloor, VanityFloor, ShowerFloor, ShowerWall, BathWall, Walls, Trim, WindowTrim, Counters, Backsplash, AppliedProducts, CabinetHardware, KitchenHardware, BathHardware, Carpet } from '../../shared';
import styles from './VisualizerPage.module.css';

export default function VisualizerPage() {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Get URL params
    const { floorplanId, elevationId } = params;
    
    // Try to get data from location.state (fresh navigation) or Redux (persisted/refresh)
    const locationState = location.state || {};
    
    // Redux state
    const { rooms, steps, elevations, combinedFloorplans, dataLoaded } = useSelector(state => state.floorplan);
    const { 
        activeScene, 
        activeSteps, 
        currentStepIndex,
        showAppliedProducts,
        stepSelections,
        sceneSelStrings,
        activeElevation: persistedElevation
    } = useSelector(state => state.visualizer);
    
    // Selectors
    const currentStep = useSelector(selectCurrentStep);
    const progressPercentage = useSelector(selectProgressPercentage);

    // Reconstruct floorplan and elevation from URL params if not in location.state
    const floorplan = useMemo(() => {
        if (locationState.floorplan) return locationState.floorplan;
        
        // floorplanId is the array index from useFloorplanFilters (no Id column in Excel)
        // Try index lookup first, then fall back to name match
        if (combinedFloorplans.length > 0) {
            const index = parseInt(floorplanId);
            if (!isNaN(index) && combinedFloorplans[index]) {
                return combinedFloorplans[index];
            }
            // Fall back to name match
            return combinedFloorplans.find(fp => 
                (fp.Name || fp.name) === floorplanId
            );
        }
        return null;
    }, [locationState.floorplan, combinedFloorplans, floorplanId]);

    const elevation = useMemo(() => {
        if (locationState.elevation) return locationState.elevation;
        
        // Try to find elevation from Redux store using URL param
        if (elevations.length > 0) {
            const foundElevation = elevations.find(elev => 
                String(elev.Id || elev.id) === String(elevationId)
            );
            if (foundElevation) {
                return {
                    id: foundElevation.Id || foundElevation.id,
                    name: foundElevation.Name || foundElevation.name,
                    image: foundElevation.Thumb || foundElevation.thumb || '',
                    originalData: foundElevation
                };
            }
        }
        
        // Fallback to persisted elevation
        return persistedElevation;
    }, [locationState.elevation, elevations, elevationId, persistedElevation]);

    // Redirect to floorplans page only if there's truly no elevation data at all
    useEffect(() => {
        if (dataLoaded && !elevation && !persistedElevation) {
            console.warn('No elevation found, redirecting to floorplans');
            navigate('/floorplans');
        }
    }, [dataLoaded, navigate]); // Intentionally minimal deps — only check once after data loads

    // Set active elevation when component mounts or elevation changes
    useEffect(() => {
        if (elevation && steps.length > 0) {
            // Only set if no persisted elevation yet (fresh navigation, not shared link)
            if (!persistedElevation || persistedElevation.id !== elevation.id) {
                console.log('Setting active elevation from URL/state:', elevation.id);
                dispatch(setActiveElevation({ elevation, steps }));
            }
        }
    }, [elevation, steps, dispatch, persistedElevation]);

    const handleMoreInfo = () => {
        console.log('More info clicked for:', floorplan);
        // TODO: Implement more info modal or navigation
    };

    const handleRoomSelect = (room) => {
        console.log('Room selected - Area:', room.area);
        console.log('Room selected - Specifier:', room.specifier);
        console.log('Room selected - Original Sel:', room.sel);
        
        // Get the latest saved sel string for this area, or use original
        const savedSel = sceneSelStrings?.[room.area];
        const selToUse = savedSel || room.sel;
        console.log('Room selected - Using Sel:', selToUse);
        
        // Create the new scene object with the correct sel
        const newScene = { ...room, sel: selToUse };
        dispatch(setActiveScene(newScene));
        
        // Find the first step that matches this room's area
        const firstStepIndex = activeSteps.findIndex(step => 
            step.room.toLowerCase() === room.area.toLowerCase()
        );
        
        if (firstStepIndex !== -1) {
            console.log('Jumping to first step for room:', room.area, 'at index:', firstStepIndex);
            dispatch(jumpToStep(firstStepIndex));
        }
    };

    // Extract just the numbers from the formatted strings
    const extractNumber = (str) => {
        if (!str) return '';
        const match = str.match(/^(\d+\+?)/);
        return match ? match[1] : str;
    };

    const extractSqft = (str) => {
        if (!str) return '';
        const match = str.match(/^([\d,]+\+?)/);
        return match ? match[1] : str;
    };

    // Get all rooms for the selected elevation
    const availableRooms = useMemo(() => {
        if (!elevation || !rooms || rooms.length === 0) return [];
        
        return rooms
            .filter(room => String(room.ElevationId || room.elevationId) === String(elevation.id))
            .map(room => {
                // Normalize specifier path (replace backslashes with forward slashes)
                const spec = (room.Specifier || room.specifier || '').replace(/\\/g, '/');
                // Ensure sel is always a string (Excel might store it as a number)
                const rawSel = room.Sel || room.sel || '-1';
                const sel = String(rawSel);
                const area = room.Area || room.area || '';
                
                // Generate thumbnail URL
                let thumbnailUrl = '';
                if (spec) {
                    thumbnailUrl = `https://renderfarm1.chameleonpower.com/cham/GetImage.ashx?spec=${encodeURIComponent(spec)}&sel=${encodeURIComponent(sel)}&type=base&size=508`;
                }
                
                return {
                    id: room.ElevationId || room.elevationId,
                    area: area,
                    image: thumbnailUrl,
                    specifier: spec,
                    sel: sel,
                    originalData: room
                };
            })
            .filter(room => room.area && room.specifier);
    }, [elevation, rooms]);

    // Track if we've done the initial step jump for the scene from location.state
    const initialStepJumpDone = useRef(false);
    const initialSceneSet = useRef(false);

    // Set the initial scene on mount
    useEffect(() => {
        if (availableRooms.length > 0 && !initialSceneSet.current) {
            // Try to use scene from location.state first (user selected in modal)
            if (locationState.scene) {
                console.log('Setting initial scene from location.state:', locationState.scene.area);
                dispatch(setActiveScene(locationState.scene));
                initialSceneSet.current = true;
            } else if (!activeScene) {
                // No scene selected, use first room
                console.log('No scene in location.state, using first room');
                const firstRoom = availableRooms[0];
                const savedSel = sceneSelStrings?.[firstRoom.area];
                const roomWithSel = savedSel ? { ...firstRoom, sel: savedSel } : firstRoom;
                dispatch(setActiveScene(roomWithSel));
                initialSceneSet.current = true;
            }
        }
    }, [availableRooms, activeScene, locationState.scene, sceneSelStrings, dispatch]);

    // Separate effect to jump to the correct step once activeSteps is populated
    // This handles the case where user selected a scene in StartDesignModal
    useEffect(() => {
        if (
            locationState.scene && 
            activeSteps.length > 0 && 
            !initialStepJumpDone.current
        ) {
            const sceneRoom = locationState.scene.area;
            if (sceneRoom) {
                const firstStepIndex = activeSteps.findIndex(step => 
                    step.room.toLowerCase() === sceneRoom.toLowerCase()
                );
                if (firstStepIndex !== -1) {
                    console.log('Jumping to first step for initial scene:', sceneRoom, 'at index:', firstStepIndex);
                    dispatch(jumpToStep(firstStepIndex));
                    initialStepJumpDone.current = true;
                }
            }
        }
    }, [locationState.scene, activeSteps, dispatch]);

    // Step navigation handlers
    const handleNextStep = () => {
        dispatch(nextStep());
    };

    const handlePreviousStep = () => {
        dispatch(previousStep());
    };

    const handleViewAllApplied = () => {
        console.log('View all applied products');
        console.log('Selections:', stepSelections);
        dispatch(toggleAppliedProducts(true));
    };

    const handleCloseAppliedProducts = () => {
        dispatch(toggleAppliedProducts(false));
    };

    // Switch room when step's room changes
    useEffect(() => {
        if (currentStep && availableRooms.length > 0) {
            const stepRoom = currentStep.room;
            
            // Find the room that matches the step's room area
            const matchingRoom = availableRooms.find(room => 
                room.area.toLowerCase() === stepRoom.toLowerCase()
            );
            
            if (matchingRoom && (!activeScene || activeScene.area.toLowerCase() !== stepRoom.toLowerCase())) {
                console.log('Step changed - switching to room:', stepRoom);
                // Get the latest saved sel string for this area
                const savedSel = sceneSelStrings?.[matchingRoom.area];
                const selToUse = savedSel || matchingRoom.sel;
                console.log('Step changed - using sel:', selToUse);
                
                const newScene = { ...matchingRoom, sel: selToUse };
                dispatch(setActiveScene(newScene));
            }
        }
    }, [currentStep, availableRooms, activeScene, sceneSelStrings, dispatch]);

    // Render the appropriate step component
    const renderStep = () => {
        if (!currentStep) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>No steps available for this elevation.</p>
                </div>
            );
        }

        const { name, params } = currentStep;

        if (name === 'Roof') {
            return (
                <Roof 
                    surfaceName={params.surface || 'Roof'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Cabinets') {
            return (
                <Cabinets 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'IslandCabinets') {
            return (
                <IslandCabinets 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Siding') {
            return (
                <Siding 
                    surfaceType={params.surfaceType || 'BODY'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'SidingAccent') {
            return (
                <SidingAccent 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ExtDoors') {
            return (
                <ExtDoors 
                    surfaceType={params.surfaceType || 'Door'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ExtShutters') {
            return (
                <ExtShutters 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Stone') {
            return (
                <Stone 
                    surfaceName={params.surface || 'Stone'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Brick') {
            return (
                <Brick 
                    surfaceName={params.surface || 'Brick'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ExtTrim') {
            return (
                <ExtTrim 
                    surfaceName={params.surface || 'Trim'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ExtWindowTrim') {
            return (
                <ExtWindowTrim 
                    surfaceName={params.surface || 'Window Trim'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Floor') {
            return (
                <Floor 
                    surfaceName={params.surface || 'Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Walls') {
            return (
                <Walls 
                    surfaceName={params.surface || 'Walls'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Trim') {
            return (
                <Trim 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'WindowTrim') {
            return (
                <WindowTrim 
                    surfaceName={params.surface || 'Window'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Counters') {
            return (
                <Counters 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Backsplash') {
            return (
                <Backsplash 
                    surfaceName={params.surface || 'Backsplash'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'BathFloor') {
            return (
                <BathFloor 
                    surfaceName={params.surface || 'Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'VanityFloor') {
            return (
                <VanityFloor 
                    surfaceName={params.surface || 'Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ShowerFloor') {
            return (
                <ShowerFloor 
                    surfaceName={params.surface || 'Shower Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ShowerWall') {
            return (
                <ShowerWall 
                    surfaceName={params.surface || 'Shower Wall'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'BathWall') {
            return (
                <BathWall 
                    surfaceName={params.surface || 'Wall'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'CabinetHardware') {
            return (
                <CabinetHardware 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'KitchenHardware') {
            return (
                <KitchenHardware 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'BathHardware') {
            return (
                <BathHardware 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Carpet') {
            return (
                <Carpet 
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Generic') {
            return (
                <Generic
                    title={params.title || 'Select Product'}
                    seriesId={params.series || '8487'}
                    surfaceName={params.surface || ''}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        return null;
    };

    // Show loading if we don't have the necessary data yet
    if (!elevation) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Loading visualizer...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Left Column - 2 parts */}
            <aside className={styles.leftColumn}>
                <Progress percentage={progressPercentage} />
                {showAppliedProducts ? (
                    <AppliedProducts 
                        selections={stepSelections}
                        onClose={handleCloseAppliedProducts}
                    />
                ) : (
                    renderStep()
                )}
            </aside>

            {/* Center Column - 6 parts */}
            <main className={styles.centerColumn}>
                <TopMenu />
                {activeScene && (
                    <Visualizer 
                        specifier={activeScene.specifier}
                        sel={activeScene.sel}
                    />
                )}
            </main>

            {/* Right Column - 1 part */}
            <aside className={styles.rightColumn}>
                {floorplan && (
                    <FloorplanDataCard
                        planName={floorplan.planName || floorplan.Name || floorplan.name}
                        sqft={extractSqft(String(floorplan.sqft || floorplan.SF || floorplan.sf || ''))}
                        beds={extractNumber(String(floorplan.beds || floorplan.Beds || ''))}
                        baths={extractNumber(String(floorplan.baths || floorplan.Baths || ''))}
                        garage={extractNumber(String(floorplan.garage || floorplan.Garage || ''))}
                        stories={extractNumber(String(floorplan.stories || floorplan.Story || floorplan.story || ''))}
                        url={floorplan.originalData?.URL || floorplan.url || floorplan.URL || ''}
                        onMoreInfo={handleMoreInfo}
                    />
                )}
                
                <RoomSceneSwitcher
                    rooms={availableRooms}
                    activeRoomId={activeScene?.id}
                    onRoomSelect={handleRoomSelect}
                />
            </aside>
        </div>
    );
}
